import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { listTemplates } from "../services/templateService";
import type { Template } from "../types/template";
import { TemplateLibraryView } from "../components/organisms/TemplateLibraryView";
import { TemplateDocumentNameModal } from "../components/organisms/TemplateDocumentNameModal";
import { createDraft, getDraftFileBase64 } from "../services/generatedContractService";
import { openDraftInHost } from "../services/hostDocumentService";
import { ApiError } from "../services/apiClient";
import { useGraphTemplateLibrary } from "../config/graphConfig";
import { listGraphTemplatesForLibrary, loadFileIntoWord } from "../services/graphService";

const sessionErrorMessage =
  "Your session has expired or is not valid. Please sign in again.";

function userFacingApiMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError && err.status === 401) {
    return sessionErrorMessage;
  }
  if (
    err instanceof ApiError &&
    /missing or invalid authorization header/i.test(err.message)
  ) {
    return sessionErrorMessage;
  }
  if (err instanceof Error) {
    return err.message;
  }
  return fallback;
}

export const TemplateLibraryPage = () => {
  const navigate = useNavigate();
  const graphTemplateLibrary = useGraphTemplateLibrary();
  const [templates, setTemplates] = useState<Template[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isOpeningDraft, setIsOpeningDraft] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [documentNameForTemplateId, setDocumentNameForTemplateId] = useState<string | null>(null);

  const loadTemplates = useCallback(() => {
    setIsLoading(true);
    setError(null);
    const loader = graphTemplateLibrary ? listGraphTemplatesForLibrary() : listTemplates();
    loader
      .then(setTemplates)
      .catch((err: unknown) => {
        setError(userFacingApiMessage(err, "Unable to load templates."));
      })
      .finally(() => setIsLoading(false));
  }, [graphTemplateLibrary]);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const runDraftFlowWithName = useCallback(
    async (template: Template, displayName: string) => {
      setIsOpeningDraft(true);
      setError(null);
      try {
        if (template.graphDriveId) {
          await loadFileIntoWord(template.graphDriveId, template.id);
          return;
        }
        const draft = await createDraft(template.id, { displayName });
        const base64 = await getDraftFileBase64(draft.storedFileId);
        await openDraftInHost(base64);
        navigate(`/contractiq/templates/${template.id}/review`, {
          state: { draft, capturedDocumentName: displayName }
        });
      } catch (err: unknown) {
        const message =
          err instanceof ApiError
            ? err.message
            : err instanceof Error
              ? err.message
              : "Unable to create or open the draft in Word.";
        setError(message);
      } finally {
        setIsOpeningDraft(false);
      }
    },
    [navigate]
  );

  const handleTemplateSelect = async (id: string) => {
    const template = templates.find((t) => t.id === id);
    if (!template || isOpeningDraft) {
      return;
    }
    if (template.graphDriveId) {
      void runDraftFlowWithName(template, "");
      return;
    }
    setDocumentNameForTemplateId(id);
  };

  const documentNameOpen = documentNameForTemplateId !== null;
  const pendingTemplate =
    documentNameForTemplateId !== null
      ? templates.find((t) => t.id === documentNameForTemplateId)
      : undefined;

  const handleDocumentNameConfirm = (displayName: string) => {
    if (!pendingTemplate) {
      setDocumentNameForTemplateId(null);
      return;
    }
    setDocumentNameForTemplateId(null);
    void runDraftFlowWithName(pendingTemplate, displayName);
  };

  const handleDocumentNameCancel = () => {
    setDocumentNameForTemplateId(null);
  };

  return (
    <>
      <TemplateDocumentNameModal
        open={documentNameOpen}
        isBusy={isOpeningDraft}
        onCancel={handleDocumentNameCancel}
        onConfirm={handleDocumentNameConfirm}
      />
      <TemplateLibraryView
        templates={templates}
        isLoading={isLoading}
        isOpeningDraft={isOpeningDraft}
        error={error}
        onTemplateSelect={handleTemplateSelect}
        onRetryLoad={loadTemplates}
      />
    </>
  );
};
